import { Component, useState, ChangeEvent } from 'react';
import { Container, Form, FormGroup, Input, Label, Row, Col, Button, FormFeedback, Card, CardBody, CardTitle } from 'reactstrap';
import '../../../styles/Forms.scss'
import { ConversionServerModel, NewSplitByBusinessUnitModel, ConversionServerClient, EpicSplitClient, ConversionClient, EnvironmentModel, IUserLoginDetailsModel } from '../../../client/ApiClient';
import Toolbar from '../../basic/Toolbar';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { ApplicationState } from 'store';
import { store } from '../../../AppContext';
import { enqueueNotification } from '../../../store/Notifications';

interface FieldStatus {
    isValid: boolean;
    value: string;
    feedback: string;
}

interface EpicSplitByBusinessUnitCreateState {
    serverList: ConversionServerModel[] | null;
    server: FieldStatus;
    originalDatabaseName: FieldStatus;
    filteredDatabaseName: FieldStatus;
    agencyList: FieldStatus;
    branchList: FieldStatus;
    departmentList: FieldStatus;
    allListsEmpty: boolean;
    serverName: string;
}

type EpicSplitByBusinessUnitCreateProps = RouteComponentProps & {
    currentUser: IUserLoginDetailsModel | undefined,
};

class EpicSplitByBusinessUnitCreate extends Component<EpicSplitByBusinessUnitCreateProps, EpicSplitByBusinessUnitCreateState> {
    private conversionServerClient: ConversionServerClient = new ConversionServerClient();
    private conversionClient: ConversionClient = new ConversionClient();
    private databaseNameFeedback = 'Database Name must be 1-128 characters, start with a letter, and only contain letters, numbers, hyphens (-), or underscores (_)';
    private missingFilter = 'You must provide a valid Agency, Branch or Department list'
    private badFilder = 'Filter list must start and end with a number, can only containt numbers, spaces or commas (,), and less than 2000 characters'
    constructor(props: EpicSplitByBusinessUnitCreateProps) {
        super(props);
        this.state = {
            serverList: null,
            server: { isValid: false, value: '', feedback: 'Please select a server' },
            originalDatabaseName: { isValid: false, value: '', feedback: this.databaseNameFeedback },
            filteredDatabaseName: { isValid: false, value: '', feedback: this.databaseNameFeedback },
            agencyList: { isValid: false, value: '', feedback: this.missingFilter },
            branchList: { isValid: false, value: '', feedback: this.missingFilter },
            departmentList: { isValid: false, value: '', feedback: this.missingFilter },
            allListsEmpty: true,
            serverName: '',
        };
    }

    isFormValid(): boolean {
        return this.state.server.isValid &&
            this.state.originalDatabaseName.isValid &&
            this.state.filteredDatabaseName.isValid &&
            this.state.agencyList.isValid &&
            this.state.branchList.isValid &&
            this.state.departmentList.isValid
    }

    isNullOrEmpty(value: string): boolean {
        return value === null || value === undefined || value.toString().trim() === '';
    }

    isValidSqlServerDatabaseName(name: string): boolean {
        if (typeof name !== 'string') return false;
        if (name.trim() !== name) return false; // No leading/trailing spaces
        if (name.length < 1 || name.length > 128) return false;
        if (/[\x00-\x1F]/.test(name)) return false; // No control chars
        // First char must be a letter, rest can be a-z, A-Z, 0-9, -, _
        if (!/^[a-zA-Z][a-zA-Z0-9\-_]*$/.test(name)) return false;
        return true;
    }

    isValidFilterList(input: string): boolean {
        // Must start and end with a number, only one comma (with optional spaces) between numbers
        if (!/^[0-9]+(\s*,\s*[0-9]+)*$/.test(input.trim())) return false;
        if (input.trim().length > 2000) return false;
        return true;
    }

    componentDidMount() {
        try {
            this.conversionServerClient.listSource().then(result => {
                this.setState({ serverList: result });
            });
        } catch (err) {
            console.log(err);
        }
    }

    validateServer(event: ChangeEvent<HTMLInputElement>) {
        var state = this.state.server;
        var value = event.target.value;
        var isValid = value !== null && value !== undefined && value.toString().trim() !== '';
        state.isValid = isValid;
        state.value = value;
        const serverName = this.state.serverList!.find(s => s.id.toString() === value)?.serverInstanceName || '';
        this.setState({ server: state, serverName });
    }

    validateOriginalDatabaseName(event: ChangeEvent<HTMLInputElement>) {
        var state = this.state.originalDatabaseName;
        var value = event.target.value;
        var isValid = this.isValidSqlServerDatabaseName(value);
        state.isValid = isValid;
        state.value = value;
        this.setState({ originalDatabaseName: state });
    }

    validateFilteredDatabaseName(event: ChangeEvent<HTMLInputElement>) {
        var state = this.state.filteredDatabaseName;
        var value = event.target.value;
        var isValid = this.isValidSqlServerDatabaseName(value);
        state.isValid = isValid;
        state.value = value;
        this.setState({ filteredDatabaseName: state });
    }

    validateFilterLists(agency: string, branch: string, department: string) {
        var agencyState = this.state.agencyList;
        var branchState = this.state.branchList;
        var departmentState = this.state.departmentList;

        var agencyBlank = this.isNullOrEmpty(agency);
        var branchBlank = this.isNullOrEmpty(branch);
        var departmentBlank = this.isNullOrEmpty(department);

        agencyState.value = agency;
        branchState.value = branch;
        departmentState.value = department;

        if (agencyBlank && branchBlank && departmentBlank) {
            agencyState.isValid = false;
            agencyState.feedback = this.missingFilter;
            branchState.isValid = false;
            branchState.feedback = this.missingFilter;
            departmentState.isValid = false;
            departmentState.feedback = this.missingFilter;
        } else {
            agencyState.isValid = true;
            branchState.isValid = true;
            departmentState.isValid = true;
        }
        if (!agencyBlank && !this.isValidFilterList(agency)) {
            agencyState.isValid = false;
            agencyState.feedback = this.badFilder + " (" + agency.trim().length.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " characters)";
        }
        if (!branchBlank && !this.isValidFilterList(branch)) {
            branchState.isValid = false;
            branchState.feedback = this.badFilder + " (" + branch.trim().length.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " characters)";
        }
        if (!departmentBlank && !this.isValidFilterList(department)) {
            departmentState.isValid = false;
            departmentState.feedback = this.badFilder + " (" + department.trim().length.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " characters)";
        }

        this.setState({
            agencyList: agencyState,
            branchList: branchState,
            departmentList: departmentState
        });
    }

    validateAgencyList(event: ChangeEvent<HTMLInputElement>) {
        var value = event.target.value;
        this.validateFilterLists(value, this.state.branchList.value, this.state.departmentList.value);
    }

    validateBranchList(event: ChangeEvent<HTMLInputElement>) {
        var value = event.target.value;
        this.validateFilterLists(this.state.agencyList.value, value, this.state.departmentList.value);
    }

    validateDepartmentList(event: ChangeEvent<HTMLInputElement>) {
        var value = event.target.value;
        this.validateFilterLists(this.state.agencyList.value, this.state.branchList.value, value);
    }

    clearForm() {
        this.setState({
            server: { isValid: false, value: '', feedback: 'Please select a server' },
            originalDatabaseName: { isValid: false, value: '', feedback: this.databaseNameFeedback },
            filteredDatabaseName: { isValid: false, value: '', feedback: this.databaseNameFeedback },
            agencyList: { isValid: false, value: '', feedback: this.missingFilter },
            branchList: { isValid: false, value: '', feedback: this.missingFilter },
            departmentList: { isValid: false, value: '', feedback: this.missingFilter },
            allListsEmpty: true
        });
    }

    handleBackToList = () => {
        window.location.href = '/tools/split/epic';
    }

    splitDatabase() {
        var model = new NewSplitByBusinessUnitModel();
        model.agencyList = this.state.agencyList.value;
        model.branchList = this.state.branchList.value;
        model.departmentList = this.state.departmentList.value;
        model.filteredDatabaseName = this.state.filteredDatabaseName.value;
        model.originalDatabaseName = this.state.originalDatabaseName.value;
        model.serverId = this.state.server.value;

        var connectionCheck = new EnvironmentModel();
        connectionCheck.catalog = this.state.originalDatabaseName.value;
        connectionCheck.host = this.state.serverName;

        this.conversionClient.getCheckConnection(connectionCheck, this.props.currentUser!.emailAddress!, 'source')
            .then((response: any) => {
                if (response) {
                    let noError = true;
                    const arr = ['Agency', 'Branch', 'Department']
                    let num = 0
                    const validate = new Promise<void>((resolve, reject) => {
                        arr.map(field => {
                            const list = (field === 'Agency') ? model.agencyList : (field === 'Branch') ? model.branchList : model.departmentList;
                            if (list) {
                                this.conversionClient.businessUnitValidate(connectionCheck, 'epic', list, field)
                                    .then((message: any) => {
                                        if (message !== '') {
                                            store.dispatch(enqueueNotification({ type: 'error', message: field + ' List invalid: ' + message }))
                                            noError = false;
                                        }
                                        num += 1;
                                        if (num === 3) resolve();
                                    })
                                    .catch((err: any) => {
                                        console.log(err)
                                        reject();
                                    })
                            }
                            else {
                                num += 1;
                            }
                        });
                    })

                    validate
                        .then(() => {
                            if (noError) {
                                try {
                                    this.conversionClient.businessUnitPolicyCount(connectionCheck, 'epic', model.agencyList || '', model.branchList || '', model.departmentList || '')
                                        .then((response) => {
                                            if (confirm("Split Database will include " + response + " policies") == true) {
                                                var client = new EpicSplitClient();
                                                client.byBusinessUnit(model).then(() => this.handleBackToList());
                                            } 
                                        })
                                        .catch((err: any) => {
                                            console.log(err)
                                        })
                                } catch (err) {
                                    console.log(err);
                                }
                            }
                        });
                }
                else {
                    store.dispatch(enqueueNotification({ type: 'error', message: 'Error: Original database is invalid. Please check your email for details' }))
                }
            })
            .catch((err: any) => {
                console.log(err)
            })
    }


    public render() {
        return (
            <Container className="mt-4">
                <Row className="mb-2">
                    <Col xs={8} md={9}><h4>Create New Epic Split By Business Unit</h4></Col>
                    <Col xs={4} md={3}><Button color='secondary' className='submit-button-orange' onClick={this.handleBackToList}>Back to List</Button></Col>
                </Row>

                <Row className="mb-2">
                    <Col xs={12} md={12}>
                        <Form>
                            <FormGroup row>
                                <Label for="serverInput" sm={3}>Server</Label>
                                <Col sm={9}>
                                    <Input
                                        id="serverInput"
                                        name="server"
                                        type="select"
                                        valid={this.state.server.isValid}
                                        invalid={!this.state.server.isValid}
                                        value={this.state.server.value}
                                        onChange={e => this.validateServer(e)} >
                                        <option>Select Server</option>
                                        {this.state.serverList?.map(item => (
                                            <option key={item.id} value={item.id}>{item.serverInstanceName}</option>
                                        ))}
                                    </Input>
                                    <FormFeedback>
                                        {this.state.server.feedback}
                                    </FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="originalDatabaseNameInput" sm={3}>Original Database Name</Label>
                                <Col sm={9}>
                                    <Input
                                        id="originalDatabaseNameInput"
                                        name="originalDatabaseName"
                                        type="text"
                                        valid={this.state.originalDatabaseName.isValid}
                                        invalid={!this.state.originalDatabaseName.isValid}
                                        value={this.state.originalDatabaseName.value}
                                        onChange={e => this.validateOriginalDatabaseName(e)} />
                                    <FormFeedback>
                                        {this.state.originalDatabaseName.feedback}
                                    </FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="filteredDatabaseNameInput" sm={3}>Filtered Database Name</Label>
                                <Col sm={9}>
                                    <Input
                                        id="filteredDatabaseNameInput"
                                        name="filteredDatabaseName"
                                        type="text"
                                        valid={this.state.filteredDatabaseName.isValid}
                                        invalid={!this.state.filteredDatabaseName.isValid}
                                        value={this.state.filteredDatabaseName.value}
                                        onChange={e => this.validateFilteredDatabaseName(e)} />
                                    <FormFeedback>
                                        {this.state.filteredDatabaseName.feedback}
                                    </FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="agencyListInput" sm={3}>List of Agencies</Label>
                                <Col sm={9}>
                                    <Input
                                        id="agencyListInput"
                                        name="agencyList"
                                        type="text"
                                        valid={this.state.agencyList.isValid}
                                        invalid={!this.state.agencyList.isValid}
                                        value={this.state.agencyList.value}
                                        onChange={e => this.validateAgencyList(e)} />
                                    <FormFeedback>
                                        {this.state.agencyList.feedback}
                                    </FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="branchListInput" sm={3}>List of Branches</Label>
                                <Col sm={9}>
                                    <Input
                                        id="branchListInput"
                                        name="branchList"
                                        type="text"
                                        valid={this.state.branchList.isValid}
                                        invalid={!this.state.branchList.isValid}
                                        value={this.state.branchList.value}
                                        onChange={e => this.validateBranchList(e)} />
                                    <FormFeedback>
                                        {this.state.branchList.feedback}
                                    </FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label for="departmentListInput" sm={3}>List of Departments</Label>
                                <Col sm={9}>
                                    <Input
                                        id="departmentListInput"
                                        name="departmentList"
                                        type="text"
                                        valid={this.state.departmentList.isValid}
                                        invalid={!this.state.departmentList.isValid}
                                        value={this.state.departmentList.value}
                                        onChange={e => this.validateDepartmentList(e)} />
                                    <FormFeedback>
                                        {this.state.departmentList.feedback}
                                    </FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Col sm={{ offset: 3, size: 9 }}>
                                    <Button color="success" disabled={!this.isFormValid()} onClick={e => { e.preventDefault(); this.splitDatabase(); }}>
                                        Split Database
                                    </Button>
                                    {' '}
                                    <Button color="danger" onClick={e => { e.preventDefault(); this.clearForm(); }}>
                                        Clear
                                    </Button>
                                </Col>
                            </FormGroup>
                        </Form>
                    </Col>
                </Row>
            </Container>
        );
    }
}
export default connect(
    (state: ApplicationState) => ({
        currentUser: state.authentication
            && state.authentication.currentUser
    }),
)(withRouter(EpicSplitByBusinessUnitCreate));
